import type { Publication } from '~/interfaces/publication';

const PROPERTIES = [
  'og:title',
  'og:url',
  'og:description',
  'og:description',
  'og:image',
  'og:title',
  'og:image:width',
  'og:image:height',
  'og:image:alt',
  'og:type',
  'og:site_name',
  'article:author',
];

const NAMES = [
  'keywords',
  'author',
  'robots',
  'viewport',
  'description',
  'twitter:title',
  'twitter:url',
  'twitter:description',
  'twitter:image',
  'twitter:card',
  'fb:app_id',
];

type AllowedOverrideKeys = (typeof PROPERTIES)[number] | (typeof NAMES)[number];

interface AdditionalLink {
  tagName: string;
  rel: string;
  href: string;
  as: string;
}

interface RootMetaItem {
  [key: string]: string;
}

interface GetMergedMetaDataOptions {
  canonicalUrl?: string;
  matches: any[];
  overrides?: Partial<Record<AllowedOverrideKeys, string>>;
  links?: AdditionalLink[];
  returnEarlyCheck?: boolean;
  structuredData?: any;
}

const getMergedMetaData = ({
  canonicalUrl,
  matches,
  overrides = {},
  links = [],
  returnEarlyCheck = false,
  structuredData,
}: GetMergedMetaDataOptions) => {
  const root = matches.find((match: any) => match?.id === 'root');
  const publication: Publication | undefined = root?.data?.publication;
  const title = publication?.name || overrides?.title || '';
  const description = publication?.description || overrides?.description || '';

  // Meta data in child routes will override their parent's meta data
  // This insures we merged in the relevant meta data from the root route
  const rootMeta: RootMetaItem[] | undefined = root?.meta;
  const dynamicLinks: RootMetaItem[] = [];
  const rootMetaMerged: RootMetaItem =
    rootMeta?.reduce((acc, obj) => {
      if (obj.tagName === 'link') {
        dynamicLinks.push(obj);
        return acc;
      }

      const key = Object.keys(obj)[0];
      const value = obj[key];
      acc[key] = value;
      return acc;
    }, {}) || {};

  if (returnEarlyCheck || !publication) {
    return [];
  }

  const metaData: RootMetaItem = {
    ...rootMetaMerged,
    title: title,
    description: description,
    'og:type': 'website',
    'og:url': publication.url,
    'og:title': title,
    'og:description': description,
    'og:site_name': publication.name,
    'og:image': publication.thumbnail.url,
    'og:image:width': '1200',
    'og:image:height': '630',
    'og:image:alt': description,
    'twitter:url': publication.url,
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': publication.thumbnail.url,
    'twitter:card': 'summary_large_image',
    'fb:app_id': `${root?.data?.facebookAppId}`,
    ...overrides,
  };

  const metaDataArray: Array<any> = Object.keys(metaData).map((key) => {
    return { [key]: metaData[key] };
  });

  let restructuredArray: Array<any> = metaDataArray.map((item) => {
    const key = Object.keys(item)[0];
    if (PROPERTIES.includes(key)) {
      return {
        property: key,
        content: item[key],
      };
    }

    if (NAMES.includes(key)) {
      return {
        name: key,
        content: item[key],
      };
    }

    return item;
  });

  // Add canonical link if it exists
  if (canonicalUrl) {
    restructuredArray.push({
      tagName: 'link',
      rel: 'canonical',
      href: canonicalUrl,
    });
  }

  // Merge in dynamic links
  restructuredArray.push(...dynamicLinks);
  if (links.length > 0) {
    restructuredArray.push(...links);
  }

  // Add structured data if it exists
  if (structuredData) {
    const structuredDataBlocks = Array.isArray(structuredData) ? structuredData : [structuredData];
    for (const block of structuredDataBlocks) {
      restructuredArray.push({
        'script:ld+json': {
          ...block,
        },
      });
    }
  }

  // create a set of unique meta tags where key is JSON.stringify of meta
  const uniqueMeta = new Set<string>(restructuredArray.map((item) => JSON.stringify(item)));

  restructuredArray = Array.from(uniqueMeta).map((item) => JSON.parse(item));

  return restructuredArray;
};

export default getMergedMetaData;
